import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { buildCurrentLocationPath, buildRouteWithContinueUrl } from '../utils/authContinueUrl';
import { peekSessionExpired } from '../utils/authSessionFlags';

const PrivateRoute = ({ children }) => {
  const { currentUser, isLoading } = useAuth();
  const location = useLocation();
  const continuePath = buildCurrentLocationPath(location);

  // Still loading auth state
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>;
  }

  // No user, redirect to login page. If the session dropped unexpectedly
  // (flagged by AuthContext), add ?reason=session-expired so LoginPage can
  // explain why instead of rendering a bare login form. Peek (don't clear) so
  // render stays pure; LoginPage consumes the flag on mount.
  if (!currentUser) {
    const loginRoute = buildRouteWithContinueUrl('/login', continuePath);
    const to = peekSessionExpired()
      ? `${loginRoute}${loginRoute.includes('?') ? '&' : '?'}reason=session-expired`
      : loginRoute;
    return <Navigate to={to} />;
  }

  // STRICT: Firebase Auth emailVerified is the single source of truth for
  // verification (ADR-077 supersedes ADR-015's verify-later pattern). This
  // blocks ALL unverified users regardless of Firestore status.
  if (!currentUser.emailVerified) {
    return <Navigate to={buildRouteWithContinueUrl('/verify-email', continuePath)} />;
  }

  // NOTE: we intentionally do NOT add a secondary redirect on a stale
  // Firestore `userStatus === 'pending_verification'` here. AuthContext loads
  // userStatus with a one-shot getDoc per auth-state change, so a user who
  // verifies their email in-session keeps a stale 'pending_verification'
  // status until the next reload. Blocking on it created an infinite
  // /verify-email <-> /dash loop: VerifyEmail sees emailVerified:true and
  // routes to the app, PrivateRoute saw stale Firestore status and bounced
  // back. Firebase Auth is the SSOT (checked above); AuthContext already
  // self-heals the Firestore status to 'active' when it detects verification.
  return children;
};

export default PrivateRoute;
